'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { WHATSAPP_CONTACTS, openWhatsAppChat } from '@/lib/whatsappContacts';
import { trackEvent } from '@/lib/analytics';

export default function WhatsAppButton() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showSelector, setShowSelector] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    setShowSelector(true);
  };

  const handleContactSelect = (number: string, message: string) => {
    // Track WhatsApp click
    trackEvent('whatsapp_click', {
      event_category: 'Contact',
      event_label: 'Floating Button',
      contact_method: 'WhatsApp'
    });
    
    openWhatsAppChat(number, message);
    setShowSelector(false);
  };

  if (!isHydrated) {
    return (
      <button
        className="fixed bottom-6 right-6 w-14 h-14 bg-success text-success-foreground rounded-full shadow-lg flex items-center justify-center z-50"
        aria-label="WhatsApp Contact"
      >
        <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
      </button>
    );
  }

  return (
    <>
      {/* WhatsApp Number Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-text-primary/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full border border-border animate-slideUp">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-text-primary flex items-center gap-2">
                  <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" className="text-success" />
                  Choose Department
                </h3>
                <button
                  onClick={() => setShowSelector(false)}
                  className="p-2 hover:bg-surface rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" className="text-text-secondary" />
                </button>
              </div>
              <p className="text-sm text-text-secondary mt-2">
                Select which department you'd like to contact via WhatsApp
              </p>
            </div>
            
            <div className="p-6 space-y-3">
              {WHATSAPP_CONTACTS.map((contact, index) => (
                <button
                  key={index}
                  onClick={() => handleContactSelect(contact.number, contact.message)}
                  className="w-full block p-4 bg-surface hover:bg-primary/5 border-2 border-border hover:border-primary rounded-xl transition-all duration-300 group text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-success/10 text-success rounded-lg group-hover:bg-success group-hover:text-success-foreground transition-colors">
                      <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-text-primary group-hover:text-primary transition-colors">
                        {contact.label}
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {contact.description}
                      </div>
                    </div>
                    <Icon 
                      name="ArrowTopRightOnSquareIcon" 
                      size={20} 
                      variant="outline" 
                      className="text-text-secondary group-hover:text-primary transition-colors"
                    />
                  </div>
                </button>
              ))}
            </div>

            <div className="p-6 pt-0">
              {/* Info box removed */}
            </div>
          </div>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      <button
        onClick={handleClick}
        className={`fixed bottom-6 right-6 w-14 h-14 bg-success text-success-foreground rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-50 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        aria-label="WhatsApp Contact"
      >
        <Icon name="ChatBubbleLeftRightIcon" size={24} variant="solid" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-error rounded-full animate-pulse" />
      </button>
    </>
  );
}